import React, {
  createContext, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { AuthContext } from 'util/Auth';
import * as api from 'util/api';

export const PermissionsContext = createContext({
  permissions: [], loading: true, refetch: () => {}, canManageRoles: false,
});

const fetchPermissions = (setState) => {
  setState((s) => ({ ...s, loading: true }));
  api.getMyPermissions()
    .then((permissions) => {
      setState({ permissions: Array.isArray(permissions) ? permissions : [], loading: false });
    })
    .catch(() => {
      setState({ permissions: [], loading: false });
    });
};

export const PermissionsProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [state, setState] = useState({ permissions: [], loading: true });

  const refetch = () => {
    if (!currentUser) return;
    fetchPermissions(setState);
  };

  useEffect(() => {
    if (!currentUser) {
      setState({ permissions: [], loading: false });
      return;
    }
    fetchPermissions(setState);
  }, [currentUser ? currentUser.uid : null]);

  const isPermissionAdmin = Array.isArray(state.permissions)
    && state.permissions.includes('permission-admin');
  const canManageRoles = isPermissionAdmin || !!currentUser?.admin;

  return (
    <PermissionsContext.Provider value={{
      ...state, refetch, canManageRoles,
    }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

PermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Can user access this route item?
 * - requiresAdmin: allow if userIsAdmin OR has permission OR in whitelist
 * - otherwise: allow if no gate, or in emailWhitelist, or has item.permission
 * - item.permission may be a string or an array of strings (user needs any one)
 */
export const userCanAccessRoute = (item, userEmail, permissions, userIsAdmin) => {
  if (!item) return true;
  if (item.requiresAdmin && userIsAdmin) return true;
  const hasWhitelist = item.emailWhitelist && Array.isArray(item.emailWhitelist);
  let permissionKeys = [];
  if (Array.isArray(item.permission)) {
    permissionKeys = item.permission;
  } else if (item.permission) {
    permissionKeys = [item.permission];
  }
  const hasPermissionReq = permissionKeys.length > 0;
  if (!hasWhitelist && !hasPermissionReq && !item.requiresAdmin) return true;
  if (item.requiresAdmin && !userIsAdmin && !hasWhitelist && !hasPermissionReq) return false;
  if (hasWhitelist && userEmail && item.emailWhitelist.includes(userEmail)) return true;
  if (hasPermissionReq && permissions && permissionKeys.some((k) => permissions.includes(k))) return true;
  return false;
};
